<template>
	<div class="table-overflow-hide">
		<div class="directoryTable scrollable-55vh">
			<table class="table" data-cy="leads-table">
				<thead class="sticky-header">
					<tr>
						<th class="badge-col"></th>
						<th @click="sortBy('trial_id')" class="sortable-header">
							Lead ID
							<span v-if="sortKey === 'trial_id'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === "asc" ? "mdi-arrow-up" : "mdi-arrow-down" }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('school_name')" class="sortable-header">
							School Name
							<span v-if="sortKey === 'school_name'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === "asc" ? "mdi-arrow-up" : "mdi-arrow-down" }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('district_name')" class="sortable-header">
							District
							<span v-if="sortKey === 'district_name'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === "asc" ? "mdi-arrow-up" : "mdi-arrow-down" }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('request_type')" class="sortable-header col-center">
							Type
							<span v-if="sortKey === 'request_type'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === "asc" ? "mdi-arrow-up" : "mdi-arrow-down" }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('created_tstamp')" class="sortable-header">
							Created Date
							<span v-if="sortKey === 'created_tstamp'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === "asc" ? "mdi-arrow-up" : "mdi-arrow-down" }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('demo_date')" class="sortable-header col-center">
							Demo Date
							<span v-if="sortKey === 'demo_date'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === "asc" ? "mdi-arrow-up" : "mdi-arrow-down" }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('trial_date')" class="sortable-header">
							Trial Date
							<span v-if="sortKey === 'trial_date'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === "asc" ? "mdi-arrow-up" : "mdi-arrow-down" }}
								</v-icon>
							</span>
						</th>
						<th
							@click="sortBy('status')"
							class="sortable-header"
							data-cy="lead-status-sort"
						>
							Status
							<span v-if="sortKey === 'status'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === "asc" ? "mdi-arrow-up" : "mdi-arrow-down" }}
								</v-icon>
							</span>
						</th>
						<th class="icon-col" style="text-align: right">
							<v-btn
								@click="reload"
								icon
								color="gray200"
								size="x-small"
								:loading="leadsStore.loading"
								style="max-width: 20px; max-height: 20px"
								data-cy="reload-leads-btn"
							>
								<v-icon>mdi-reload</v-icon>
							</v-btn>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="lead in paginatedResults" :key="lead.trial_id" class="table-row">
						<td class="badge-col">
							<v-tooltip v-if="lead.status === 'converted'" text="Customer" location="top">
								<template #activator="{ props: tipProps }">
									<span
										v-bind="tipProps"
										class="status-icon-badge status-icon-badge--customer"
										:data-cy="`lead-customer-badge-${lead.trial_id}`"
									>
										<v-icon size="16" color="success">mdi-account-check</v-icon>
									</span>
								</template>
							</v-tooltip>
							<v-tooltip v-else-if="isSuppressed(lead)" text="Suppressed" location="top">
								<template #activator="{ props: tipProps }">
									<span
										v-bind="tipProps"
										class="status-icon-badge status-icon-badge--suppressed"
										:data-cy="`lead-suppressed-badge-${lead.trial_id}`"
									>
										<v-icon size="16" color="grey">mdi-eye-off-outline</v-icon>
									</span>
								</template>
							</v-tooltip>
						</td>
						<td>
							<span
								class="lead-id-link"
								@click="editLead(lead)"
								:data-cy="`lead-id-link-${lead.trial_id}`"
								:title="'Edit ' + lead.trial_id"
							>
								{{ lead.trial_id }}
							</span>
						</td>
						<td data-cy="table-col-school-name">{{ lead.school_name }}</td>
						<td>{{ lead.district_name }}</td>
						<td class="col-center">
							<div
								class="request-type-chips"
								:data-cy="`request-type-chips-${lead.trial_id}`"
							>
								<v-tooltip
									v-for="chip in requestTypeChips(lead)"
									:key="chip.label"
									:text="chip.label"
									location="top"
								>
									<template #activator="{ props: tipProps }">
										<span
											v-bind="tipProps"
											class="status-icon-badge"
											:class="`status-icon-badge--${chip.label.toLowerCase()}`"
											:data-cy="`request-type-chip-${chip.label.toLowerCase()}-${lead.trial_id}`"
										>
											<v-icon size="16" :color="chip.color">{{ chip.icon }}</v-icon>
										</span>
									</template>
								</v-tooltip>
							</div>
						</td>
						<td>{{ formatDate(lead.created_tstamp) }}</td>
						<td class="col-center">
							<v-chip
								v-if="lead.demo_date"
								color="teal"
								size="small"
								class="cursor-pointer"
								@click="openDemoScheduleModal(lead)"
								:data-cy="`demo-date-chip-${lead.trial_id}`"
								title="Reschedule demo"
							>
								{{ formatDemoDate(lead.demo_date) }} ET
							</v-chip>
							<v-btn
								v-else
								density="compact"
								icon
								variant="text"
								@click="openDemoScheduleModal(lead)"
								:data-cy="`schedule-demo-btn-${lead.trial_id}`"
								title="Schedule demo"
							>
								<v-icon color="gray500">mdi-calendar-plus</v-icon>
							</v-btn>
						</td>
						<td>
							<v-chip
								v-if="lead.trial_date"
								color="info"
								size="small"
								:class="{ 'cursor-pointer': canInitializeTrial(lead) }"
								:data-cy="`trial-date-chip-${lead.trial_id}`"
								:title="canInitializeTrial(lead) ? 'Fulfill trial' : 'Trial given'"
								@click="canInitializeTrial(lead) && openFulfillTrialModal(lead)"
							>
								{{ formatDemoDate(lead.trial_date) }}
							</v-chip>
							<v-btn
								v-else
								size="small"
								variant="outlined"
								@click="openCreateTrialModal(lead)"
								:data-cy="`create-trial-btn-${lead.trial_id}`"
								title="Create trial"
								prepend-icon="mdi-gift-open-outline"
							>
								Trial
							</v-btn>
						</td>
						<td>
							<v-chip
								:color="getStatusColor(lead.status)"
								size="small"
								class="cursor-pointer"
								@click="openStatusModal(lead)"
								:data-cy="`lead-status-chip-${lead.trial_id}`"
								title="Change status"
							>
								{{ leadStatusLabel(lead.status) }}
							</v-chip>
						</td>
						<td class="actions-cell">
							<v-btn-group density="compact" variant="outlined" divided>
								<v-btn
									size="small"
									:loading="quoteGeneratingId === lead.trial_id"
									:disabled="quoteGeneratingId !== null"
									@click="openQuoteGenerateModal(lead)"
									:data-cy="`generate-quote-btn-${lead.trial_id}`"
									title="Generate quote"
									prepend-icon="mdi-file-document-plus"
								>
									Quote
								</v-btn>
								<v-btn
									size="small"
									:loading="quoteDownloadingId === lead.trial_id"
									:disabled="quoteDownloadingId !== null"
									@click="downloadQuote(lead)"
									:data-cy="`download-quote-btn-${lead.trial_id}`"
									title="Download quote"
									icon
								>
									<v-icon>mdi-download</v-icon>
								</v-btn>
								<v-menu location="bottom end">
									<template #activator="{ props: menuProps }">
										<v-btn
											size="small"
											v-bind="menuProps"
											icon
											:data-cy="`lead-actions-menu-${lead.trial_id}`"
											title="More actions"
										>
											<v-icon>mdi-dots-vertical</v-icon>
										</v-btn>
									</template>
									<v-list density="compact">
										<v-list-item
											v-if="!isSuppressed(lead)"
											:data-cy="`suppress-lead-btn-${lead.trial_id}`"
											@click="confirmSuppress(lead)"
										>
											<v-list-item-title>Suppress</v-list-item-title>
										</v-list-item>
										<v-list-item
											v-if="isSuppressed(lead)"
											:data-cy="`restore-lead-btn-${lead.trial_id}`"
											@click="restoreLead(lead)"
										>
											<v-list-item-title>Restore</v-list-item-title>
										</v-list-item>
										<v-list-item
											v-if="lead.status !== 'converted'"
											:data-cy="`mark-customer-btn-${lead.trial_id}`"
											@click="confirmMarkCustomer(lead)"
										>
											<v-list-item-title>Mark as customer</v-list-item-title>
										</v-list-item>
									</v-list>
								</v-menu>
							</v-btn-group>
						</td>
					</tr>
				</tbody>
			</table>
			<div
				v-if="leadsStore.loading && !leadsStore.leads.length"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<LoadingState text="Loading Leads"></LoadingState>
			</div>
			<div
				v-if="!leadsStore.loading && !filteredData.length"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<div class="empty-state">
					<figure style="max-width: 300px">
						<IconListEmptyState />
					</figure>
					<div class="title">No records found.</div>
					<p>Try another filter or search.</p>
				</div>
			</div>
		</div>
	</div>
	<div class="table-pagination-btns">
		<pagination
			v-model:curr-page="currPage"
			v-model:per-page="perPage"
			:page-status-text="pageStatusText"
			:total-pages="totalPages"
			@page-down="pageDown"
			@page-up="pageUp"
		></pagination>
	</div>

	<LeadEditModal v-model="showEditModal" :lead="selectedLead" />
	<CreateTrialFromLeadModal
		v-model="showCreateTrialModal"
		:lead="selectedCreateTrialLead"
	/>
	<QuoteGenerateModal
		v-model="showQuoteGenerateModal"
		:trial="selectedLeadForQuote"
		:loading="quoteGeneratingId !== null"
		@submit="onQuoteGenerateSubmit"
	/>
	<DemoScheduleModal
		v-model="showDemoScheduleModal"
		:trial="selectedDemoLead"
		:schedule-fn="scheduleDemoForLead"
	/>
	<LeadStatusModal v-model="showStatusModal" :lead="selectedStatusLead" />

	<v-dialog v-model="showFulfillTrialConfirm" max-width="460" data-cy="fulfill-trial-confirm-modal">
		<v-card>
			<v-card-title>Fulfill trial?</v-card-title>
			<v-card-text>
				This provisions the school and marks the trial fulfilled.
				<template v-if="fulfillTargetLead">
					<br />
					<strong>{{ fulfillTargetLead.school_name }}</strong>
					({{ fulfillTargetLead.trial_id }})
				</template>
			</v-card-text>
			<v-card-actions>
				<v-spacer />
				<v-btn
					variant="text"
					data-cy="fulfill-trial-cancel-btn"
					@click="showFulfillTrialConfirm = false"
				>
					Cancel
				</v-btn>
				<v-btn
					color="primary"
					variant="flat"
					data-cy="fulfill-trial-confirm-btn"
					@click="fulfillTrial"
				>
					Fulfill
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>

	<v-dialog v-model="showSuppressConfirm" max-width="420" data-cy="suppress-lead-confirm-modal">
		<v-card>
			<v-card-title>Suppress lead?</v-card-title>
			<v-card-text>
				This hides the lead from the Leads list. You can restore it later from Suppressed.
			</v-card-text>
			<v-card-actions>
				<v-spacer />
				<v-btn variant="text" data-cy="suppress-cancel-btn" @click="showSuppressConfirm = false">
					Cancel
				</v-btn>
				<v-btn
					color="error"
					variant="flat"
					:loading="statusUpdating"
					data-cy="suppress-confirm-btn"
					@click="suppressLead"
				>
					Suppress
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>

	<v-dialog v-model="showCustomerConfirm" max-width="460" data-cy="mark-customer-confirm-modal">
		<v-card>
			<v-card-title>Mark as customer?</v-card-title>
			<v-card-text>
				This marks the lead as converted. If a school exists, it may be set to active.
			</v-card-text>
			<v-card-actions>
				<v-spacer />
				<v-btn variant="text" data-cy="mark-customer-cancel-btn" @click="showCustomerConfirm = false">
					Cancel
				</v-btn>
				<v-btn
					color="success"
					variant="flat"
					:loading="statusUpdating"
					data-cy="mark-customer-confirm-btn"
					@click="markAsCustomer"
				>
					Mark as customer
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import usePaginate from "@/hooks/usePaginate";
import pagination from "@/components/pagination.vue";
import { complexSearch } from "@/utils";
import LoadingState from "@/components/loadingState.vue";
import IconListEmptyState from "@/assets/icons/list-empty-state.svg";
import dayjs from "dayjs";
import FetchService from "@/services/fetch.service";
import { useAlertsStore } from "@/stores/alerts";
import { useAuthenticationStore } from "@/stores/authentication";
import useLeads from "@/stores/support/leads";
import type { TrialMembership } from "@/stores/support/trials";
import LeadEditModal from "@/components/support/LeadEditModal.vue";
import CreateTrialFromLeadModal from "@/components/support/CreateTrialFromLeadModal.vue";
import QuoteGenerateModal from "@/components/support/QuoteGenerateModal.vue";
import DemoScheduleModal from "@/components/support/DemoScheduleModal.vue";
import LeadStatusModal from "@/components/support/LeadStatusModal.vue";
import {
  matchesLeadFilter,
  isSuppressed,
  canInitializeTrial,
  leadStatusLabel,
  DEFAULT_LEAD_FILTER,
  type LeadFilter,
} from "@/components/support/leadsUtils";

const props = defineProps<{
  search: string;
  filter?: LeadFilter;
}>();

defineEmits<{
  (e: "update:search", ...args: any[]): void;
}>();

const leadsStore = useLeads();
const alertsStore = useAlertsStore();

const quoteGeneratingId = ref<string | null>(null);
const quoteDownloadingId = ref<string | null>(null);

const showQuoteGenerateModal = ref(false);
const selectedLeadForQuote = ref<TrialMembership | null>(null);

const showEditModal = ref(false);
const selectedLead = ref<TrialMembership | null>(null);

const showCreateTrialModal = ref(false);
const selectedCreateTrialLead = ref<TrialMembership | null>(null);

const showDemoScheduleModal = ref(false);
const selectedDemoLead = ref<TrialMembership | null>(null);

const showStatusModal = ref(false);
const selectedStatusLead = ref<TrialMembership | null>(null);

const showFulfillTrialConfirm = ref(false);
const fulfillTargetLead = ref<TrialMembership | null>(null);

const showSuppressConfirm = ref(false);
const showCustomerConfirm = ref(false);
const statusTargetLead = ref<TrialMembership | null>(null);
const statusUpdating = ref(false);

const sortKey = ref<string>("");
const sortOrder = ref<"asc" | "desc">("asc");

/** IDs / subdomain must match the search query exactly (not as a substring). */
const EXACT_MATCH_FIELDS = [
  "trial_id",
  "subdomain",
  "school_id",
  "school_district_id",
  "district_id",
] as const;

const matchesExactId = (lead: TrialMembership, search: string) => {
  const query = search.trim().toLowerCase();
  if (!query) return false;

  return EXACT_MATCH_FIELDS.some((key) => {
    const value = lead[key as keyof TrialMembership];
    if (value == null || value === "") return false;
    return String(value).toLowerCase() === query;
  });
};

const matchesFuzzyFields = (lead: TrialMembership, search: string) => {
  const exactKeys = new Set<string>(EXACT_MATCH_FIELDS);
  const haystack = Object.entries(lead)
    .filter(([key, value]) => !exactKeys.has(key) && value != null && value !== "")
    .map(([, value]) => String(value))
    .join(" ");
  return complexSearch(search, haystack);
};

onMounted(() => {
  leadsStore.loadLeads();
});

const filteredData = computed(() => {
  const filter = props.filter || DEFAULT_LEAD_FILTER;

  let filtered = leadsStore.leads.filter((lead) => {
    if (!matchesLeadFilter(lead, filter)) return false;

    if (props.search) {
      return matchesExactId(lead, props.search) || matchesFuzzyFields(lead, props.search);
    }
    return true;
  });

  if (sortKey.value) {
    filtered = filtered.sort((a, b) => {
      let aValue = a[sortKey.value as keyof TrialMembership] as
        | string
        | number
        | null
        | undefined;
      let bValue = b[sortKey.value as keyof TrialMembership] as
        | string
        | number
        | null
        | undefined;

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortOrder.value === "asc" ? 1 : -1;
      if (bValue == null) return sortOrder.value === "asc" ? -1 : 1;

      if (sortKey.value === "created_tstamp" || sortKey.value === "demo_date" || sortKey.value === "trial_date") {
        aValue = new Date(aValue as string).getTime();
        bValue = new Date(bValue as string).getTime();
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortOrder.value === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder.value === "asc" ? 1 : -1;
      return 0;
    });
  }

  return filtered;
});

const { currPage, perPage, pageStatusText, totalPages, pageDown, pageUp, paginatedResults } =
  usePaginate(filteredData);

const formatDate = (date: string) => dayjs(date).format("MM/DD/YYYY HH:mm");
const formatDemoDate = (date: string) => dayjs(date).format("MM/DD/YY h:mma");

const getStatusColor = (status: string) => {
  switch (status) {
    case "requested":
      return "info";
    case "pending":
      return "warning";
    case "fullfilled":
    case "fulfilled":
    case "converted":
      return "success";
    case "expired":
    case "cancelled":
    case "rejected":
      return "error";
    default:
      return "default";
  }
};

type RequestTypeChip = {
  label: "Trial" | "Demo";
  color: string;
  icon: string;
};

const requestTypeChips = (lead: TrialMembership): RequestTypeChip[] => {
  switch (lead.request_type) {
    case "trial":
      return [{ label: "Trial", color: "info", icon: "mdi-account-clock-outline" }];
    case "demo":
      return [{ label: "Demo", color: "teal", icon: "mdi-presentation-play" }];
    case "both":
      return [
        { label: "Trial", color: "info", icon: "mdi-account-clock-outline" },
        { label: "Demo", color: "teal", icon: "mdi-presentation-play" },
      ];
    case "lead":
    default:
      return [];
  }
};

const sortBy = (key: string) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
  } else {
    sortKey.value = key;
    sortOrder.value = "asc";
  }
};

const reload = () => {
  leadsStore.loadLeads();
};

const editLead = (lead: TrialMembership) => {
  selectedLead.value = { ...lead };
  showEditModal.value = true;
};

const openCreateTrialModal = (lead: TrialMembership) => {
  selectedCreateTrialLead.value = { ...lead };
  showCreateTrialModal.value = true;
};

const openQuoteGenerateModal = (lead: TrialMembership) => {
  selectedLeadForQuote.value = { ...lead };
  showQuoteGenerateModal.value = true;
};

const openDemoScheduleModal = (lead: TrialMembership) => {
  selectedDemoLead.value = { ...lead };
  showDemoScheduleModal.value = true;
};

const openStatusModal = (lead: TrialMembership) => {
  selectedStatusLead.value = { ...lead };
  showStatusModal.value = true;
};

const openFulfillTrialModal = (lead: TrialMembership) => {
  fulfillTargetLead.value = lead;
  showFulfillTrialConfirm.value = true;
};

const fulfillTrial = () => {
  if (!fulfillTargetLead.value) return;
  const trialId = fulfillTargetLead.value.trial_id;
  const baseUrl = (import.meta.env.VITE_BASE_URL as string | undefined)?.replace(/\/$/, "") || "";
  window.open(`${baseUrl}/api/v1/trial/${trialId}`, "_blank", "noopener");
  alertsStore.addAlert("success", "Trial initialization started", 5);
  showFulfillTrialConfirm.value = false;
  fulfillTargetLead.value = null;
  leadsStore.loadLeads();
};

const confirmSuppress = (lead: TrialMembership) => {
  statusTargetLead.value = lead;
  showSuppressConfirm.value = true;
};

const confirmMarkCustomer = (lead: TrialMembership) => {
  statusTargetLead.value = lead;
  showCustomerConfirm.value = true;
};

const suppressLead = async () => {
  if (!statusTargetLead.value) return;
  statusUpdating.value = true;
  const result = await leadsStore.updateLead(statusTargetLead.value.trial_id, {
    status: "rejected",
  });
  statusUpdating.value = false;
  if (result.status) {
    alertsStore.addAlert("success", "Lead suppressed");
    showSuppressConfirm.value = false;
    statusTargetLead.value = null;
  } else {
    alertsStore.addAlert("error", result.message || "Failed to suppress lead");
  }
};

const markAsCustomer = async () => {
  if (!statusTargetLead.value) return;
  statusUpdating.value = true;
  const result = await leadsStore.updateLead(statusTargetLead.value.trial_id, {
    status: "converted",
  });
  statusUpdating.value = false;
  if (result.status) {
    alertsStore.addAlert("success", "Lead marked as customer");
    showCustomerConfirm.value = false;
    statusTargetLead.value = null;
  } else {
    alertsStore.addAlert("error", result.message || "Failed to mark as customer");
  }
};

const restoreLead = async (lead: TrialMembership) => {
  statusUpdating.value = true;
  const result = await leadsStore.updateLead(lead.trial_id, { status: "requested" });
  statusUpdating.value = false;
  if (result.status) {
    alertsStore.addAlert("success", "Lead restored");
  } else {
    alertsStore.addAlert("error", result.message || "Failed to restore lead");
  }
};

const scheduleDemoForLead = (trialId: string, demoDate: string) =>
  leadsStore.scheduleDemo(trialId, demoDate);

const onQuoteGenerateSubmit = async (payload: {
  trial_id: string;
  duration: number;
  blackbaud: boolean;
  discount?: number;
  notes: string;
}) => {
  quoteGeneratingId.value = payload.trial_id;
  try {
    useAuthenticationStore().loadToken();
    const res = await FetchService.fetchData("/quote/generate", "post", payload);
    quoteGeneratingId.value = null;
    if (res === false) {
      alertsStore.addAlert("error", "Session expired or unauthorized. Please log in again.");
      return;
    }
    if (res && (res as { success?: boolean }).success && (res as { file?: string }).file) {
      showQuoteGenerateModal.value = false;
      selectedLeadForQuote.value = null;
      alertsStore.addAlert(
        "success",
        "Quote generated successfully. Use Download quote to get the PDF."
      );
      return;
    }
    const errMsg =
      (res as { error?: string })?.error ||
      (res as { message?: string })?.message ||
      "Failed to generate quote.";
    alertsStore.addAlert("error", errMsg);
  } catch (e) {
    quoteGeneratingId.value = null;
    console.error("Generate quote error:", e);
    alertsStore.addAlert("error", "An unexpected error occurred while generating the quote.");
  }
};

const downloadQuote = async (lead: TrialMembership) => {
  quoteDownloadingId.value = lead.trial_id;
  try {
    useAuthenticationStore().loadToken();
    const res = await FetchService.fetchData(`/quote/${lead.trial_id}`, "get");
    quoteDownloadingId.value = null;
    if (res === false) {
      alertsStore.addAlert("error", "Session expired or unauthorized. Please log in again.");
      return;
    }
    const url = res && (res as { url?: string }).url;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
      alertsStore.addAlert("success", "Quote download started.");
      return;
    }
    const errMsg =
      (res as { error?: string })?.error ||
      (res as { message?: string })?.message ||
      "Failed to get download URL. Generate a quote first.";
    alertsStore.addAlert("error", errMsg);
  } catch (e) {
    quoteDownloadingId.value = null;
    console.error("Download quote error:", e);
    alertsStore.addAlert("error", "An unexpected error occurred while downloading the quote.");
  }
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;

.icon-col {
	min-width: 120px;
}

.badge-col {
	width: 28px;
	min-width: 28px;
	padding-right: 0 !important;
	text-align: center;
	vertical-align: middle;
}

.status-icon-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 22px;
	height: 22px;
	border-radius: 50%;

	&--customer {
		background-color: rgba(var(--v-theme-success), 0.12);
	}

	&--suppressed {
		background-color: rgba(0, 0, 0, 0.06);
	}

	&--trial {
		background-color: rgba(var(--v-theme-info), 0.12);
	}

	&--demo {
		background-color: rgba(32, 201, 151, 0.12);
	}
}

.actions-cell {
	white-space: nowrap;
}

.sortable-header {
	cursor: pointer;
	user-select: none;
	transition: background-color 0.2s;

	&:hover {
		background-color: rgba(0, 0, 0, 0.05);
	}
}

.sort-indicator {
	margin-left: 4px;
	display: inline-block;
}

.table-row {
	td {
		transition: background-color 0.3s;
	}
	&:hover {
		td {
			background-color: #f5f5f5;
		}
	}
}

.cursor-pointer {
	cursor: pointer;
}

.request-type-chips {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: center;
	gap: 2px;
}

.col-center {
	text-align: center;
	vertical-align: middle;
}

.lead-id-link {
	font-family: monospace;
	font-size: 0.75rem;
	color: rgb(var(--v-theme-primary));
	cursor: pointer;
	text-decoration: underline;
	text-underline-offset: 2px;

	&:hover {
		opacity: 0.75;
	}
}
</style>
